/**
 * Hinge-load statics for the control-surface mechanism — deliberately small, pure and TESTED,
 * because the sign↔direction mapping is exactly the kind of bookkeeping an LLM gets wrong. The
 * UI tooltip and the LLM report both consume these so the *physical* conclusion ("tends to
 * stow / deploy") is computed once, here, rather than re-derived in prose.
 *
 * Convention (matches the rest of the tool): x aft, z up; pitching-moment coefficient CMy is
 * NOSE-UP-POSITIVE (consistent with M_c/4 = CMy0 + 0.25·CFz); deploy = trailing edge DOWN.
 */
export interface ElemLoad { CFx: number; CFz: number; CMy: number; }
export type V2 = [number, number];

/** Pitching-moment coefficient about `pivot`, transferred from the origin:
 *  M(P) = CMy0 − z_P·CFx + x_P·CFz. */
export function momentAboutPivot(l: ElemLoad, pivot: V2): number {
  return l.CMy - pivot[1] * l.CFx + pivot[0] * l.CFz;
}

/** Sum several element loads (e.g. the vane+flaperon the deploy hinge carries). */
export function sumLoads(loads: ElemLoad[]): ElemLoad {
  return loads.reduce((a, l) => ({ CFx: a.CFx + l.CFx, CFz: a.CFz + l.CFz, CMy: a.CMy + l.CMy }),
    { CFx: 0, CFz: 0, CMy: 0 });
}

/**
 * Aerodynamic tendency implied by a hinge moment, in this tool's convention. A NEGATIVE
 * (nose-down) hinge moment rotates the trailing edge UP — i.e. toward retraction — so it tends
 * to STOW; a positive (nose-up, TE-down) moment tends to DEPLOY. (Equivalently: lift acting
 * AFT of the hinge gives a negative moment and stows; lift forward of the hinge deploys.)
 */
export function hingeTendency(hingeMoment: number, eps = 1e-6): 'stow' | 'deploy' | 'neutral' {
  if (Math.abs(hingeMoment) < eps) return 'neutral';
  return hingeMoment < 0 ? 'stow' : 'deploy';
}

/** A short, unambiguous phrase for the tendency + what the actuator must do to DEPLOY. */
export function hingePhrase(hingeMoment: number): string {
  const t = hingeTendency(hingeMoment);
  if (t === 'neutral') return 'aero hinge moment ≈ 0 (neutral)';
  if (t === 'stow') return 'aero tends to STOW (TE-up); the actuator must push to deploy';
  return 'aero tends to DEPLOY (TE-down); the actuator must hold it back';
}
